import { v4 as uuidv4 } from 'uuid'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'

import {
  Table,
  TableBody,
  Box,
  TableContainer,
  alpha,
  Button,
} from '@mui/material'
import { BalanceReportLibraryColumnProps } from '@/state/balance-report/models'
import { dateTimeTableFormat } from '@/constants/date'

import { colors } from '@/theme/colors.theme'
import PaginationTable from '@/views/components/pagination-table/index.component'
import NoContentAvailable from '@/views/components/no-content-available/index.component'

import useBalanceReportTable from '@/views/balance-report/hooks/use-balance-report-table.hooks'
import useBalanceReportFilters from '@/views/balance-report/hooks/use-balance-report-filters.hooks'
import BalanceReportTableHead from './head.component'
import BalanceReportTableRow from './row.component'

const BalanceReportTableComponent = () => {
  const { t } = useTranslation()

  const {
    sortedRows,
    createSortHandler,
    order,
    orderBy,
    headCells,
    headXlsxCells,
    pagination,
    getFormattedRow,
  } = useBalanceReportTable()

  const {
    filters,
    handleChangePage,
    handleChangeLimit,
    downloadXlsxSearchFilter,
  } = useBalanceReportFilters()

  const fileName = `${t('reports.balanceReport')}-${format(
    new Date(),
    dateTimeTableFormat,
  )}`

  const xlsxButton = (
    <Button
      onClick={() =>
        downloadXlsxSearchFilter(
          { ...filters, page: 1, limit: 999999, xlsx: true },
          headXlsxCells,
          fileName,
          getFormattedRow,
        )
      }
      variant="contained"
    >
      {t('labels.downloadAsXLS')}
    </Button>
  )

  if (sortedRows.length === 0) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        flexGrow={1}
        justifyContent="center"
        border={1}
        borderColor={alpha(colors.white, 0.3)}
        py={5}
      >
        <NoContentAvailable
          title={t('messages.noData')}
          instruction={t('messages.noResultsFound')}
        />
      </Box>
    )
  }

  return (
    <>
      <TableContainer>
        <Table>
          <BalanceReportTableHead
            order={order}
            orderBy={orderBy}
            createSortHandler={createSortHandler}
            headCells={headCells}
          />
          <TableBody>
            {sortedRows.map((row: BalanceReportLibraryColumnProps) => (
              <BalanceReportTableRow key={uuidv4()} row={row} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <PaginationTable
        pagination={pagination}
        leftContent={xlsxButton}
        handleChangePage={handleChangePage}
        handleChangeLimit={handleChangeLimit}
      />
    </>
  )
}

export default BalanceReportTableComponent
